import { FC, useCallback, useEffect, useMemo } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { VALIDATION } from "../../constants/validation";
import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { InvalidValue } from "../InvalidValue";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { DateSerializer } from "./DateSerializer";
import { DateboxBaseProps } from "./Datebox";
import { Input } from "./Input";
import { errorMessages } from "./errorMessages";

/**
 * The `Field` component properties.
 */
export interface FieldProps extends FieldBaseProps<string | InvalidValue | null>, DateboxBaseProps {
  /**
   * The date serializer to use. If not specified defaults to `DateSerializer.Iso`.
   */
  readonly serializer?: DateSerializer;
}

/**
 * A React Hook Form compatible Datebox Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, required = false, defaultValue = null, zone = "utc", validator, serializer = DateSerializer.Iso, ...restProps } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const validateHandler = useCallback(
    (value: string | InvalidValue | null, formValues: FieldValues) => {
      if (required) {
        if (value === null) return VALIDATION.REQUIRED;

        if (typeof value === "string" && value.length === 0) return VALIDATION.REQUIRED;
      }

      if (value !== null) {
        const valid = serializer.valid(value);
        if (!valid) {
          if (typeof value === "string") {
            return "Data error, see console";
          } else if (value.type === "invalid") {
            return (value.reason && errorMessages[value.reason]) || errorMessages["unparsable"];
          }
        }
      }

      return validator ? validator(value, formValues) : true;
    },
    [required, validator, serializer, zone],
  );

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validateHandler,
    }),
    [disabled, readOnly, validateHandler],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  // Let's help people diagnose problems.
  useEffect(() => {
    if (value === null) return;

    const valid = serializer.valid(value);
    if (!valid && typeof value === "string") {
      console.error(`Field [${name}] assigned value unsupported by serializer, check that serializer setting matches data format.`);
    }
  }, [value === null || typeof value === "string" ? value : undefined]);

  const changeHandler = useCallback(
    (newValue: Date | InvalidValue | null) => {
      onChange(serializer.to(newValue));
    },
    [onChange, serializer],
  );

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={ref}
          value={serializer.from(value, zone)}
          required={required}
          zone={zone}
          defaultValue={serializer.from(defaultValue, zone)}
          onChange={changeHandler}
          onFocusExit={onBlur}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
